import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ChevronLeft, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { ErrorState } from '@heyroot/ui/primitives/ErrorState';
import { Skeleton } from '@heyroot/ui/primitives/Skeleton';
import { Textarea } from '@heyroot/ui/primitives/Textarea';
import { Modal } from '@heyroot/backoffice-ui';
import { authenticatedFetch } from '../lib/authenticated-fetch';
import { useAdminAuth } from '../lib/auth-context';
import { PERMS } from '../../rbac/permissions';
import {
  ACCOUNTING_MODULE_LABEL,
  ACCOUNTING_MODULE_DESCRIPTION,
  ACCOUNTING_MODULE_DEPENDENCIES_LABEL,
  ACCOUNTING_MODULE_TIER_LABEL,
} from '../lib/accounting-module-metadata';
import { GuidedActivationDrawer } from '../components/accounting/GuidedActivationDrawer';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AccountingModule {
  moduleKey: string;
  tier: number;
  status: string;
  effectiveStatus: string;
  activatedAt: string | null;
  activatedByAdminUid: string | null;
  lastTransitionAt: string;
  lastTransitionReason: string | null;
  failureCode: string | null;
  failureMessage: string | null;
}

interface ModuleEvent {
  id: string;
  fromStatus: string | null;
  toStatus: string;
  actorAdminUid: string;
  requestId: string;
  reason: string | null;
  createdAt: string;
}

// ─── API helpers ──────────────────────────────────────────────────────────────

async function apiJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail = '';
    try {
      const body = (await res.json()) as { message?: string; error?: string };
      detail = body.message ?? body.error ?? '';
    } catch {
      // non-JSON body
    }
    throw new Error(detail ? `${res.status}: ${detail}` : `Request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

async function fetchModules(tenantId: string): Promise<AccountingModule[]> {
  const res = await apiJson<{ modules: AccountingModule[] }>(
    await authenticatedFetch(`/api/tenants/${encodeURIComponent(tenantId)}/accounting-modules`),
  );
  return res.modules;
}

async function fetchModuleEvents(tenantId: string, moduleKey: string): Promise<ModuleEvent[]> {
  const res = await apiJson<{ events: ModuleEvent[] }>(
    await authenticatedFetch(
      `/api/tenants/${encodeURIComponent(tenantId)}/accounting-modules/${encodeURIComponent(moduleKey)}/events`,
    ),
  );
  return res.events;
}

async function postModuleAction(
  tenantId: string,
  moduleKey: string,
  action: 'activate' | 'deactivate' | 'retry',
  body: { reason: string; requestId: string },
): Promise<unknown> {
  return apiJson(
    await authenticatedFetch(
      `/api/tenants/${encodeURIComponent(tenantId)}/accounting-modules/${encodeURIComponent(moduleKey)}/${action}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    ),
  );
}

// ─── Query keys ───────────────────────────────────────────────────────────────

export const modulesQueryKey = (tenantId: string) => ['accounting-modules', tenantId] as const;
export const eventsQueryKey = (tenantId: string, moduleKey: string) =>
  ['accounting-module-events', tenantId, moduleKey] as const;

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ effectiveStatus }: { effectiveStatus: string }) {
  const { variant, label } = (() => {
    switch (effectiveStatus) {
      case 'active':
        return { variant: 'success' as const, label: 'Active' };
      case 'blocked':
        return { variant: 'warning' as const, label: 'Blocked' };
      case 'kill_switch':
        return { variant: 'danger' as const, label: 'Kill Switch' };
      default:
        return { variant: 'neutral' as const, label: 'Inactive' };
    }
  })();
  return (
    <Badge variant={variant} shape="status" size="sm">
      {label}
    </Badge>
  );
}

function FailedBadge() {
  return (
    <Badge variant="danger" shape="status" size="sm">
      Failed
    </Badge>
  );
}

// ─── Confirmation dialog ───────────────────────────────────────────────────────

type ActionKind = 'activate' | 'deactivate' | 'retry';

interface ConfirmState {
  moduleKey: string;
  action: ActionKind;
  requestId: string;
}

function ConfirmationDialog({
  state,
  tenantId,
  onClose,
  onSuccess,
}: {
  state: ConfirmState;
  tenantId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      postModuleAction(tenantId, state.moduleKey, state.action, {
        reason,
        requestId: state.requestId,
      }),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : 'Action failed.');
    },
  });

  const isActivate = state.action === 'activate' || state.action === 'retry';
  const moduleLabel = ACCOUNTING_MODULE_LABEL[state.moduleKey] ?? state.moduleKey;
  const title =
    state.action === 'activate'
      ? `Activate ${moduleLabel}`
      : state.action === 'deactivate'
        ? `Deactivate ${moduleLabel}`
        : `Retry ${moduleLabel}`;

  return (
    <Modal
      open
      onClose={onClose}
      role="alertdialog"
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant={state.action === 'deactivate' ? 'danger' : 'primary'}
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || reason.trim().length === 0}
          >
            {mutation.isPending ? 'Submitting…' : 'Confirm'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-body-sm text-secondary">
          Tenant: <span className="font-mono text-caption">{tenantId}</span>
        </p>
        {isActivate && (
          <div className="rounded-control border border-warning bg-warning-pale text-warning-text text-body-sm p-3 flex flex-col gap-1">
            <p className="text-body-sm-emphasis">Before you activate</p>
            <p>This will begin writing GL entries for this module immediately.</p>
            <p>
              <strong>Note:</strong> most consumers ack+discard when inactive — there is no
              automatic backlog replay. If a backfill is required, that is a separate operation.
            </p>
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="activation-reason" className="text-eyebrow uppercase text-muted">
            Reason <span className="text-danger-text">*</span>
          </label>
          <Textarea
            id="activation-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Brief justification for this change…"
            rows={3}
            disabled={mutation.isPending}
          />
        </div>
        {error && (
          <p role="alert" className="text-body-sm text-danger-text">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

// ─── Event history expandable ──────────────────────────────────────────────────

function EventHistory({ tenantId, moduleKey }: { tenantId: string; moduleKey: string }) {
  const { data, isPending, isError } = useQuery({
    queryKey: eventsQueryKey(tenantId, moduleKey),
    queryFn: () => fetchModuleEvents(tenantId, moduleKey),
  });

  if (isPending) return <Skeleton className="h-10 w-full" />;
  if (isError) return <p className="text-body-sm text-danger-text">Failed to load events.</p>;
  if (!data || data.length === 0)
    return <p className="text-caption text-muted">No events recorded yet.</p>;

  return (
    <div className="flex flex-col gap-1">
      {data.map((evt) => (
        <div key={evt.id} className="flex items-start gap-3 text-caption text-muted">
          <span className="whitespace-nowrap shrink-0">
            {new Date(evt.createdAt).toLocaleString()}
          </span>
          <span className="shrink-0 font-mono">
            {evt.fromStatus ?? '—'} → {evt.toStatus}
          </span>
          <span className="truncate" title={evt.reason ?? undefined}>
            {evt.reason ?? <em className="not-italic text-muted">no reason</em>}
          </span>
          <span className="shrink-0 font-mono text-muted/60">{evt.actorAdminUid}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Module card ──────────────────────────────────────────────────────────────

function ModuleCard({
  mod,
  tenantId,
  canWrite,
  onAction,
}: {
  mod: AccountingModule;
  tenantId: string;
  canWrite: boolean;
  onAction: (moduleKey: string, action: ActionKind) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const isTransitioning = mod.status === 'activating' || mod.status === 'deactivating';
  const isFailed = mod.status === 'failed';
  const canActivate = (mod.status === 'inactive' || mod.status === 'failed') && !isTransitioning;
  const canDeactivate = mod.status === 'active' && !isTransitioning;
  const canRetry = isFailed;

  const label = ACCOUNTING_MODULE_LABEL[mod.moduleKey] ?? mod.moduleKey;
  const description = ACCOUNTING_MODULE_DESCRIPTION[mod.moduleKey] ?? '';
  const deps = ACCOUNTING_MODULE_DEPENDENCIES_LABEL[mod.moduleKey] ?? [];

  return (
    <div className="rounded-control border border-border bg-surface-elevated p-4 flex flex-col gap-3">
      {/* Header row */}
      <div className="flex items-start gap-3">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-body-emphasis text-primary">{label}</span>
            {isFailed ? <FailedBadge /> : <StatusBadge effectiveStatus={mod.effectiveStatus} />}
          </div>
          {deps.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-caption text-muted">Requires:</span>
              {deps.map((d) => (
                <Badge key={d} variant="info" size="sm">
                  {d}
                </Badge>
              ))}
            </div>
          )}
          <p className="text-body-sm text-secondary mt-0.5">{description}</p>
          {mod.failureCode && (
            <p className="text-caption text-danger-text">
              {mod.failureCode}: {mod.failureMessage}
            </p>
          )}
        </div>
        {/* Actions */}
        {canWrite && (
          <div className="flex items-center gap-2 shrink-0">
            {isTransitioning ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted" aria-label="In progress" />
            ) : canRetry ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => onAction(mod.moduleKey, 'retry')}
              >
                Retry
              </Button>
            ) : canActivate ? (
              <Button
                size="sm"
                variant="primary"
                onClick={() => onAction(mod.moduleKey, 'activate')}
              >
                Activate
              </Button>
            ) : canDeactivate ? (
              <Button
                size="sm"
                variant="danger"
                onClick={() => onAction(mod.moduleKey, 'deactivate')}
              >
                Deactivate
              </Button>
            ) : null}
          </div>
        )}
      </div>

      {/* Footer row */}
      <div className="flex items-center justify-between border-t border-border pt-2">
        <div className="flex items-center gap-2 text-caption text-muted">
          <span>Last transition:</span>
          <span>{new Date(mod.lastTransitionAt).toLocaleDateString()}</span>
          {mod.lastTransitionReason && (
            <span className="max-w-[300px] truncate" title={mod.lastTransitionReason}>
              — {mod.lastTransitionReason}
            </span>
          )}
        </div>
        {/* eslint-disable-next-line @heyroot/heyroot-ui/no-one-off-ui -- icon+text expand toggle; Button variant resets conflict with inline caption row layout */}
        <button
          className="flex items-center gap-1 text-caption text-muted hover:text-primary transition-colors"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
          History
        </button>
      </div>

      {/* Event history */}
      {expanded && (
        <div className="bg-surface-sunken rounded-control p-3">
          <EventHistory tenantId={tenantId} moduleKey={mod.moduleKey} />
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function AccountingModulesPage({ tenantId }: { tenantId: string }) {
  const { hasPerm } = useAdminAuth();

  if (!hasPerm(PERMS.ACCOUNTING_MODULES_READ)) {
    return <div className="p-8 text-muted">You don&#39;t have access to this page.</div>;
  }

  return <AccountingModulesContent tenantId={tenantId} />;
}

function AccountingModulesContent({ tenantId }: { tenantId: string }) {
  const { hasPerm } = useAdminAuth();
  const queryClient = useQueryClient();
  const canWrite = hasPerm(PERMS.ACCOUNTING_MODULES_WRITE);

  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const [guidedOpen, setGuidedOpen] = useState(false);

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: modulesQueryKey(tenantId),
    queryFn: () => fetchModules(tenantId),
  });

  function openDialog(moduleKey: string, action: ActionKind) {
    setConfirmState({ moduleKey, action, requestId: crypto.randomUUID() });
  }

  function invalidateModules() {
    void queryClient.invalidateQueries({ queryKey: modulesQueryKey(tenantId) });
  }

  if (isError) return <ErrorState onRetry={() => void refetch()} />;

  const modules = data ?? [];

  const byTier = new Map<number, AccountingModule[]>();
  for (const mod of modules) {
    const arr = byTier.get(mod.tier) ?? [];
    arr.push(mod);
    byTier.set(mod.tier, arr);
  }

  const hasInactive = modules.some((m) => m.status === 'inactive' || m.status === 'failed');

  return (
    <div className="flex flex-col gap-6 max-w-[900px]">
      {/* Breadcrumb */}
      <div>
        <Link
          to="/tenants/$tenantId"
          params={{ tenantId }}
          className="inline-flex items-center gap-1 text-body-sm text-secondary no-underline hover:text-primary transition-colors duration-[var(--duration-fast)]"
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
          Tenant
        </Link>
      </div>

      {/* Page header */}
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-heading-2 text-primary">Accounting Modules</h1>
        {canWrite && hasInactive && (
          <Button variant="primary" onClick={() => setGuidedOpen(true)}>
            Guided Activation
          </Button>
        )}
      </div>

      {/* Empty state */}
      {!isPending && modules.length === 0 && (
        <EmptyState
          title="No accounting modules"
          description="No accounting module rows found for this tenant."
        />
      )}

      {/* Tier sections */}
      {isPending
        ? Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex flex-col gap-3">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-20 w-full" />
            </div>
          ))
        : [1, 2, 3, 4, 5, 6].map((tier) => {
            const tierModules = byTier.get(tier) ?? [];
            if (tierModules.length === 0) return null;
            return (
              <div key={tier} className="flex flex-col gap-3">
                <h2 className="text-body-emphasis text-muted">
                  Tier {tier} — {ACCOUNTING_MODULE_TIER_LABEL[tier] ?? ''}
                </h2>
                {tierModules.map((mod) => (
                  <ModuleCard
                    key={mod.moduleKey}
                    mod={mod}
                    tenantId={tenantId}
                    canWrite={canWrite}
                    onAction={openDialog}
                  />
                ))}
              </div>
            );
          })}

      {/* Confirmation dialog */}
      {confirmState && (
        <ConfirmationDialog
          state={confirmState}
          tenantId={tenantId}
          onClose={() => setConfirmState(null)}
          onSuccess={invalidateModules}
        />
      )}

      <GuidedActivationDrawer
        open={guidedOpen}
        onClose={() => setGuidedOpen(false)}
        tenantId={tenantId}
        modules={modules}
        onSuccess={invalidateModules}
      />
    </div>
  );
}
